<script lang="ts" setup>
    import { onMounted, ref, reactive, computed } from 'vue';
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import Combo from "../../components/Combo.vue";
    import TextInput from "../../components/TextInput.vue";
    import DatePickerNoTZ from '../../components/DatePickerNoTZ.vue';
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'
    import ScheduleMessage from "../../components/Communication/ScheduleMessage.vue";  
    import DropDownSplitButton from '../../components/DropDownSplitButton.vue'
    import DropDownButtonItem from '../../components/DropDownButtonItem.vue'
    import { usePrivilegesStore } from '../../lib/Stores/PrivilegesStore'    
    import { initPinia } from '../../lib/PiniaMgr';
    import { ICol, RenderModeType, IRowSelection } from "../../lib/ChoiceTableHelpers";
    import { IRecallSearchItem, IRecallStatusItem, IRecallPrimaryIdentifierItem, IRecallSearchInputs, IRecallResourceItem, IRecallSelectedItem } from '../../lib/Models/RecallModel';
    import { ILetterTemplateItem } from '../../lib/Models/LetterTemplateModel';
    import { TableRowType, ButtonDisplaySize, TableRowHighlight } from "../../Enums/UiEnums";
    import { ActionButtonStyle } from "../../lib/Types/Types";
    import { NoTzDate } from "../../lib/DateUtils"
    import { PrivilegeType, AppScreens } from '../../Enums/ChoiceEnums';
    import * as Conversions from "../../lib/Conversions";
    import * as Validation from "../../lib/Validation";
    import { recall } from '../../lib/ChoiceApi';
    import '../../../css/choice.css';
    import _ from 'lodash';
    import * as Export from '../../lib/Export';

    const emit = defineEmits<{
        (e: "onRecallSearchItemClick", rowData?: IRowSelection): void,
    }>();
    defineExpose({refreshSearchItem});

    const tableData = ref<IRecallSearchItem[]>([]);
    const windowHeight = ref<number>();
    const scheduleMsg = ref<typeof ScheduleMessage>();
    const privilegesStore = usePrivilegesStore(initPinia());

    const formData = reactive<{
        chartNumber?: number,
        lastName?: string,
        firstName?: string,
        beginDate?: NoTzDate,
        endDate?: NoTzDate,
        status?: number,
        primaryId?: string,
        primaryIdType?: string,
        maxRows: number,
        resourceEid?: number,
        currentRow?: IRowSelection & IRecallSearchItem,
        selectedRow: IRecallSelectedItem[],
        letterTemplateId?: number
    }>({
        maxRows: 50,
        selectedRow: []
    });

    const tableColumns = ref<ICol[]>([
        { Name: '', DataField:"selected", RenderMode: RenderModeType.Selection },
        { Name: 'Appointment Date', DataField: 'appointmentDate' },
        { Name: 'Patient Chart', DataField: 'patientId' },
        { Name: 'Last Name', DataField: 'lastName' },
        { Name: 'First Name', DataField: 'firstName' },
        { Name: 'Resource Name', DataField: 'recallResourceName' },
        { Name: 'Due Date', DataField: 'recallDueDate' },
        { Name: 'Status', DataField: 'recallStatusDesc' },
        { Name: 'Home Phone', DataField: 'phoneHome' },
        { Name: 'Work Phone', DataField: 'phoneWork' },
        { Name: 'Other Phone', DataField: 'phoneOther' },
        { Name: 'App. Type', DataField: 'recallAppointmentTypeDesc' },
        { Name: 'Previous Date', DataField: 'previousDate' },
        { Name: 'Previous Resource', DataField: 'previousResource' },
        { Name: 'Previous Location', DataField: 'previousLocation' },
        { Name: 'Next Date', DataField: 'nextDate' },
        { Name: 'Next Resource', DataField: 'nextResource' },
        { Name: 'Next Location', DataField: 'nextLocation' },
    ]);
    const SearchTable = ref();

    const props = defineProps<{
        statusList: IRecallStatusItem[],
        primaryIdentifierList: IRecallPrimaryIdentifierItem[],
        resourceList: IRecallResourceItem[],
        letterSchedulerList: ILetterTemplateItem[]
    }>();

    onMounted(async () => {
        windowHeight.value = window.innerHeight - 270;
        window.addEventListener('resize', () => {
            windowHeight.value = window.innerHeight - 270;
        });
        await privilegesStore.getScreens([AppScreens.RecallSetup]);
    });

    const rowSelected = computed<boolean>(() => formData.currentRow != undefined ? true : false);
    const rowChecked = computed<boolean>(() => formData.selectedRow.length > 0 ? true : false);
    const letterSelected = computed<boolean>(() => formData.letterTemplateId != undefined ? true : false);
    const searchRowsExist = computed<boolean>(() => tableData.value.length > 0 ? true : false);
    const writePrivilege = computed<boolean>(() => privilegesStore.screens[AppScreens.RecallSetup] >= PrivilegeType.ReadUpdateWrite ? true : false);
    const updatePrivilege = computed<boolean>(() => privilegesStore.screens[AppScreens.RecallSetup] >= PrivilegeType.ReadUpdate ? true : false);

    async function onSearch() {
        tableData.value = [];
        formData.currentRow = undefined;
        SearchTable.value.clearSelectedRow();
        formData.selectedRow = [];
        const post_data = {
            patientId: formData.chartNumber,
            lastName: formData.lastName,
            firstName: formData.firstName,
            primaryIdType: formData.primaryIdType,
            primaryIdValue: formData.primaryId,
            beginDate: formData.beginDate,
            endDate: formData.endDate,
            recallStatusCode: formData.status,
            resourceEid: formData.resourceEid,
            maxRows: formData.maxRows
        };
       tableData.value = await recall.search(post_data);
    }
    async function onClear() {
        formData.chartNumber = undefined;
        formData.status = undefined;
        formData.lastName = "";
        formData.firstName = "";
        formData.resourceEid = undefined;
        formData.beginDate = "";
        formData.endDate = "";
        formData.maxRows = 50;
        formData.primaryIdType = "";
        formData.primaryId = "";
        formData.letterTemplateId = undefined;
        tableData.value = [];
        formData.currentRow = undefined;
        SearchTable.value.clearSelectedRow();
        formData.selectedRow = [];
    }

    async function onNew() {
        emit("onRecallSearchItemClick");
    }

    async function onOpen() {
        emit("onRecallSearchItemClick", formData.currentRow);
    }

    async function onOpenPatient() {
        if (!formData.currentRow)
            return;
        const row = formData.currentRow;
        scheduleMsg.value?.openPatientSetup({patientEid: row.patientEid});
    }

    async function onSchedule() {
        if (!formData.currentRow)
            return;
        const row = formData.currentRow;
        scheduleMsg.value?.loadReminderSchedule({patientEid: row.patientEid, resourceId: row.recallResourceId, scheduleDate: row.recallDueDate, recallReminderId: row.recallReminderId });
    }

    async function onPrintLetter() {
        if (formData.selectedRow.length == 0)
            return;
        if (!formData.letterTemplateId)
            return;
        const printData = JSON.parse(JSON.stringify({letterTemplateId: formData.letterTemplateId, printList: formData.selectedRow}));
        scheduleMsg.value?.printLetter(printData);
    }

    function onRowDoubleClick(rowData : IRowSelection,  _rowType: TableRowType, _evt: MouseEvent) : void {
        formData.currentRow = <IRecallSearchItem> rowData;
        emit("onRecallSearchItemClick", rowData);
    }
    function onRowClick(rowData :  IRowSelection,  _rowType: TableRowType, _evt: MouseEvent) : void {
        formData.currentRow = <IRecallSearchItem> rowData;
    }
    function onSelectChanged(checked: boolean|undefined, colData: ICol, row?: Record<string, any>) {
        if (row) {
            const rowData = <IRecallSearchItem> row;
            if (checked) {
                formData.selectedRow?.push({patientId: rowData.patientId, recallReminderId: rowData.recallReminderId});
            } else {
                if (!formData.selectedRow)
                    return;
                const index = formData.selectedRow.findIndex(item => item.patientId == rowData.patientId && item.recallReminderId == rowData.recallReminderId);
                if (index > -1)
                    formData.selectedRow.splice(index, 1);
            }
        }
        else {
            if (checked) {
                tableData.value.forEach( (element) => {
                    formData.selectedRow?.push({patientId: element.patientId, recallReminderId: element.recallReminderId});
                }); 
            }
            else {
                formData.selectedRow = [];
            }
        }   
    } 
    function onBaseExport() {
        let rowCounter = 0;
        let exportData = tableData.value.map(item => [++rowCounter, "", item.appointmentDate, item.patientId, item.lastName, item.firstName, item.recallResourceName, item.recallDueDate, item.recallStatusDesc, item.phoneHome, item.phoneWork, item.phoneOther, item.recallAppointmentTypeDesc, item.recallDateCreated, item.recallComments, item.previousDate, item.previousResource, item.previousLocation, item.nextDate, item.nextResource, item.nextLocation]);
        exportData.splice(0, 0, ["", "Sel", "AppointmentDate", "Patient Chart", "Last Name", "First Name", "Resource Name", "Due Date", "Status", "Home Phone", "Work Phone", "Other Phone", "App. Type", "Created", "Comments", "Last Appt Date", "Last Appt Resource", "Last Appt Location", "Next Appt Date", "Next Appt Resource", "Next Appt Location"]);
        Export.ExportSheetToExcel(exportData, "BaseExport.xlsx", "Sheet0");
    }
    function onBroadcastExport() {
        let exportData = tableData.value.map(item => [item.patientId, item.firstName, item.lastName, item.birthDate, item.genderCode, item.address1, item.address2, item.city, item.state, item.zipCode, item.email, item.phoneHome, item.phoneOther, item.active]);
        exportData.splice(0, 0, ["Patient ID", "First Name", "Last Name", "DOB", "Gender", "Address1", "Address2", "City", "State", "Zip Code", "Email", "Home Phone", "Mobile Phone", "Active"]);
        Export.ExportSheetToExcel(exportData, "BroadcastExport.xlsx", "Broadcast");
    }   

    async function refreshSearchItem(recallReminderId: number) {
        const recallRow = tableData.value.filter(item => item.recallReminderId == recallReminderId);
        const post_data = {
            recallReminderId: recallReminderId,
            maxRows: 1
        };
        const recallItem = await recall.search(post_data);
        if (!recallItem)
            return;
        if (recallItem.length != 1)
            return;
        if (recallRow.length == 0) {
            // New Item - add to the beginning
            tableData.value = recallItem.concat(tableData.value);
        }
        else {
            // Item already exists
            if (recallItem[0].recallReminderId != recallRow[0].recallReminderId)
                return;
            recallRow[0].recallDueDate = recallItem[0].recallDueDate;
            recallRow[0].recallAppointmentTypeId = recallItem[0].recallAppointmentTypeId;
            recallRow[0].recallAppointmentTypeDesc = recallItem[0].recallAppointmentTypeDesc;
            recallRow[0].recallStatusCode = recallItem[0].recallStatusCode;
            recallRow[0].recallStatusDesc = recallItem[0].recallStatusDesc;
            recallRow[0].recallResourceEid = recallItem[0].recallResourceEid;
            recallRow[0].recallResourceName = recallItem[0].recallResourceName;
            recallRow[0].recallDateCreated = recallItem[0].recallDateCreated;
            recallRow[0].recallComments = recallItem[0].recallComments;
        }
    }

</script>

<style scoped>
    .fixedColumn1 {
        width: 150px;
    }
    .horizontal-scrollable {
        overflow-x: scroll;
    }
</style>

<template>
    <div class="row mt-2">
        <div class="fixedColumn1 label-isalus-small">
            Chart #
        </div>
        <div class="col label-isalus-small">
            Last Name
        </div>
        <div class="fixedColumn1 label-isalus-small">
            Begin Date
        </div>
        <div class="col label-isalus-small">
            Primary ID Type
        </div>
    </div>
    <div class="row">
        <div class="fixedColumn1">
            <TextInput 
                v-model:text-value="formData.chartNumber" 
                :normalizer="Conversions.TextToNumber" 
                :validator="Validation.InRangeFunction(1, undefined)"
                :acceptCharacter="Validation.LimitToNumberCharacters"
                :maxLength="15"
                :cssFramework = "'vanilla'" />
        </div>
        <div class="col">
            <TextInput 
                v-model:text-value="formData.lastName" 
                :maxLength="95"
                :cssFramework = "'vanilla'" />
        </div>
        <div class="fixedColumn1">
            <DatePickerNoTZ 
                v-model:value="formData.beginDate" 
                :cssFramework = "'vanilla'"
                :clearable="false" />
        </div>
        <div class="col">
            <Combo 
                :optionList="props.primaryIdentifierList"
                optionValue="identifier"
                :includeBlank="true"
                v-model:selected="formData.primaryIdType" />
        </div>
    </div>
    <div class="row mt-2">
        <div class="fixedColumn1 label-isalus-small">
            Status
        </div>
        <div class="col label-isalus-small">
            First Name
        </div>
        <div class="fixedColumn1 label-isalus-small">
            End Date
        </div>
        <div class="col label-isalus-small">
            Primary ID Value
        </div>
    </div>
    <div class="row">
        <div class="fixedColumn1">
            <Combo 
                :optionList="props.statusList"
                optionValue="recallStatusCode"
                :includeBlank="true"
                v-model:selected="formData.status" />
        </div>
        <div class="col">
            <TextInput 
                v-model:text-value="formData.firstName" 
                :maxLength="35"
                :cssFramework = "'vanilla'" />
        </div>
        <div class="fixedColumn1">
            <DatePickerNoTZ 
                v-model:value="formData.endDate" 
                :cssFramework="'vanilla'"
                :clearable="false" />
        </div>
        <div class="col">
            <TextInput 
                v-model:text-value="formData.primaryId" 
                :maxLength="30"
                :cssFramework = "'vanilla'" />
        </div>
    </div>
    <div class="row mt-2">
        <div class="fixedColumn1 label-isalus-small">
        </div>
        <div class="col label-isalus-small">
            Resource
        </div>
        <div class="fixedColumn1 label-isalus-small">
            Max Rows
        </div>
        <div class="col label-isalus-small">
        </div>
    </div>
    <div class="row">
        <div class="fixedColumn1">
        </div>
        <div class="col">
            <Combo 
                :optionList="props.resourceList"
                optionValue="resourceEid"
                optionDescription="resourceName"
                optionHeader="resourceType"
                :includeHeader="true"
                :includeBlank="true"
                v-model:selected="formData.resourceEid" />
        </div>
        <div class="fixedColumn1">
            <TextInput 
                v-model:text-value="formData.maxRows" 
                :normalizer="Conversions.TextToNumber" 
                :validator="Validation.InRangeFunction(1, undefined)"
                :acceptCharacter="Validation.LimitToNumberCharacters"
                :maxLength="8"
                :cssFramework = "'vanilla'" />
        </div>
        <div class="col">
            <ActionButtonV2 
                :action="ActionButtonStyle.Search" 
                text="Search"
                :size="ButtonDisplaySize.Small" 
                :performAction="onSearch"
                :endingPadding="1" />
            <ActionButtonV2 
                :action="ActionButtonStyle.Erase" 
                text="Clear"
                :size="ButtonDisplaySize.Small" 
                :performAction="onClear" />
        </div>
    </div>
    <div class="row mt-2">
        <div class="col">
            <p style="overflow-x: scroll">
                <ChoiceTable
                    ref="SearchTable" 
                    :columns="tableColumns" 
                    :data="tableData" 
                    :show-filters="false" 
                    :allowSorting="false"
                    :height="windowHeight"
                    :width="2500"
                    fontSize="smaller"
                    :rowHighlight="TableRowHighlight.onSingleClick"
                    @rowdblclick="onRowDoubleClick"
                    @rowclick="onRowClick"
                    @selectChanged="onSelectChanged"
                    :key-value-data-member="'recallReminderId'">Loading...
                </ChoiceTable>
            </p>
        </div>
    </div>
    <div class="row mt-2">
        <div class="col-6">
            <div class="row">
                <div class="col-auto pe-1">
                    <ActionButtonV2 
                        :action="ActionButtonStyle.Add" 
                        text="New"
                        :size="ButtonDisplaySize.Small" 
                        :performAction="onNew"
                        :disabled="!writePrivilege" />
                </div>
                <div class="col-auto ps-0 pe-1">
                    <ActionButtonV2 
                        :action="ActionButtonStyle.Edit" 
                        text="Edit"
                        :size="ButtonDisplaySize.Small" 
                        :performAction="onOpen" 
                        :disabled="!rowSelected || !updatePrivilege"/>
                </div>
                <div class="col-auto ps-0 pe-1">
                    <ActionButtonV2 
                        :action="ActionButtonStyle.Open" 
                        text="Open Patient"
                        :size="ButtonDisplaySize.Small" 
                        :performAction="onOpenPatient"
                        :disabled="!rowSelected" />
                </div>
                <div class="col-auto ps-0 pe-1">
                    <ActionButtonV2 
                        :action="ActionButtonStyle.Calendar" 
                        text="Schedule"
                        :size="ButtonDisplaySize.Small" 
                        :performAction="onSchedule"
                        :disabled="!rowSelected" />
                </div>
                <div class="col-auto ps-0 pe-1">
                    <DropDownSplitButton :baseClass="'btn btn-primary label-isalus-small'" :disabled="!searchRowsExist">
                        <template v-slot:button><i class="fa-regular fa-file-excel pe-1"></i>Export</template>
                        <DropDownButtonItem :baseClass="'dropdown-item label-isalus-small'" v-on:click="onBaseExport">
                            Base (Excel)
                        </DropDownButtonItem>
                        <DropDownButtonItem :baseClass="'dropdown-item label-isalus-small'" v-on:click="onBroadcastExport">
                            Broadcast (Excel)
                        </DropDownButtonItem>
                    </DropDownSplitButton>
                </div>
            </div>
        </div>
        <div class="col-5">
            <div class="row">
                <div class="col-auto ps-0 pe-1">
                    <Combo 
                        :optionList="props.letterSchedulerList"
                        optionValue="letterTemplateId"
                        optionDescription="letterTemplateName"
                        optionHeader="letterTypeDescription"
                        :includeHeader="true"
                        :includeBlank="true"
                        v-model:selected="formData.letterTemplateId" />
                </div>
                <div class="col-auto ps-0 pe-1">
                    <ActionButtonV2 
                        :action="ActionButtonStyle.Print" 
                        text="Print Letter"
                        :size="ButtonDisplaySize.Small" 
                        :performAction="onPrintLetter"
                        :disabled="!(rowChecked && letterSelected)" />
                </div>
            </div>
        </div>
    </div>
    <ScheduleMessage ref="scheduleMsg"/>  
</template>

